import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Sortable from "sortablejs"
import { getRequest, patchRequest, postRequest } from "@react/helpers/fetch"
import Modal      from "@react/shared/ui/Modal"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import ProductPicker from "@react/shared/ui/ProductPicker"
import ComponentPicker from "./ComponentPicker"
import MarginBadge from "./MarginBadge"
import BlueprintTree, { linesOfDetail } from "./BlueprintTree"
import { COST_CATEGORIES } from "../../form/qlines/shared/category_styles"

const LINES_GRID_COLUMNS =
  "28px minmax(140px, 1.4fr) minmax(140px, 1fr) 70px 80px 100px 100px 70px 70px 110px 36px"

const OUVRAGE = "__ouvrage__"

const clientId = () => Math.random().toString(36).slice(2, 10)

const blankLine = () => ({
  _clientId:           clientId(),
  id:                  null,
  cost_category:       "labor",
  description:         "",
  ref_product_distrib: "",
  unit:                "h",
  quantity:            1,
  unit_price:          0,
  purchase_price:      0,
  product_id:          null,
  product_name:        "",
  child_blueprint_id:   null,
  child_blueprint_name: "",
  child_line_count:     0,
  _destroy:            false,
})

const normalize = line => {
  const child         = line.child_blueprint
  const cost_category = line.cost_category || "materials"
  return {
    _clientId:           clientId(),
    id:                  line.id,
    cost_category:       cost_category,
    description:         line.description || "",
    ref_product_distrib: line.ref_product_distrib || "",
    unit:                line.unit || (cost_category === "labor" ? "h" : "u"),
    quantity:            line.quantity ?? 0,
    unit_price:          line.unit_price || 0,
    purchase_price:      line.purchase_price || 0,
    product_id:          line.product?.id || null,
    product_name:        line.product?.name || "",
    child_blueprint_id:   child?.id || null,
    child_blueprint_name: child?.name || "",
    child_line_count:     child?.line_count || 0,
    _destroy:            false,
  }
}

const isOuvrage = line => !!line.child_blueprint_id

const money = amount =>
  amount == null || isNaN(amount)
    ? "—"
    : `${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const coefFmt = coef =>
  coef == null ? "—" : `×${coef.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const amount = value => value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const FIGURE = "flex items-center justify-end px-2 text-sm tabular-nums"

// The tree also takes the unit's column: the unit goes with the quantity.
const TREE_SPAN = 4

const treeFigures = (node, total) => {
  const coef   = node.purchase_price > 0 ? node.unit_price / node.purchase_price : null
  const margin = node.unit_price > 0 ? ((node.unit_price - node.purchase_price) / node.unit_price) * 100 : 0
  return (
    <>
      <div className={`${FIGURE} gap-1 whitespace-nowrap text-slate-700`}>
        {node.quantity.toLocaleString("fr-FR", { maximumFractionDigits: 3 })}
        <span className="text-slate-400">{node.unit}</span>
      </div>
      <div className={`${FIGURE} text-slate-500`}>{amount(node.purchase_price)}</div>
      <div className={`${FIGURE} text-slate-500`}>{amount(node.unit_price)}</div>
      <div className={`${FIGURE} text-slate-500`}>{coefFmt(coef)}</div>
      <div className="flex items-center justify-end"><MarginBadge pct={margin} /></div>
      <div className={`${FIGURE} font-medium text-slate-700`}>{money(total)}</div>
      <div />
    </>
  )
}

const BlueprintFormModal = ({
  isOpen,
  mode,                // "create" | "edit" | "duplicate"
  blueprintId,         // only set in edit/duplicate mode
  showUrl,             // GET /quotes/qline_service_blueprints/:id.json
  saveUrl,             // POST url for create
  updateUrlFor,        // fn(id) → PATCH url
  detailUrlFor,        // fn(id) → GET url for a referenced ouvrage
  enabledCategories,   // optional: subset of COST_CATEGORIES the account uses
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false)
  const [saving,  setSaving]  = useState(false)

  const [ref,         setRef]         = useState("")
  const [name,        setName]        = useState("")
  const [unit,        setUnit]        = useState("u")
  const [description, setDescription] = useState("")
  const [lines,       setLines]       = useState([blankLine()])
  const [expanded,    setExpanded]    = useState({})   // clientId → child lines, or "loading"
  const descriptionRef                = useRef(null)

  // Auto-grow the description textarea to fit its content.
  useEffect(() => {
    if (!descriptionRef.current) return
    descriptionRef.current.style.height = "auto"
    descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`
  }, [description, isOpen])

  useEffect(() => {
    if (!isOpen) return
    setExpanded({})
    if (mode === "create") {
      setRef(""); setName(""); setUnit("u"); setDescription("")
      setLines([blankLine()])
      return
    }
    if ((mode === "edit" || mode === "duplicate") && blueprintId && showUrl) {
      const isDuplicate = mode === "duplicate"
      setLoading(true)
      getRequest(showUrl)
        .then(data => {
          setRef(data.ref_product_distrib || "")
          setName(isDuplicate ? `${data.name || ""}${I18n.t("quotes.qline_service_blueprints.copy_suffix")}` : (data.name || ""))
          setUnit(data.unit || "u")
          setDescription(data.description || "")
          const rows = (data.qline_breakdown_blueprints || []).map(normalize)
          if (isDuplicate) rows.forEach(row => { row.id = null })
          setLines(rows.length ? rows : [blankLine()])
        })
        .finally(() => setLoading(false))
    }
  }, [isOpen, mode, blueprintId, showUrl])

  const visibleLines = lines.filter(line => !line._destroy)

  // A mémo carries no quantity, so it prints and adds nothing.
  const rollup = useMemo(() => {
    let total = 0, cost = 0
    visibleLines.forEach(line => {
      const qty      = parseFloat(line.quantity)       || 0
      const sale     = parseFloat(line.unit_price)     || 0
      const purchase = parseFloat(line.purchase_price) || 0
      total += qty * sale
      cost  += qty * purchase
    })
    const margin    = total - cost
    const coef      = cost > 0  ? total / cost           : null
    const marginPct = total > 0 ? (margin / total) * 100 : 0
    return { total, cost, margin, coef, marginPct }
  }, [visibleLines])

  const updateLine = (id, patch) =>
    setLines(previous => previous.map(line => (line._clientId === id ? { ...line, ...patch } : line)))

  const removeLine = id => {
    setLines(previous => previous.map(line => {
      if (line._clientId !== id) return line
      return line.id ? { ...line, _destroy: true } : null
    }).filter(Boolean))
    setExpanded(previous => {
      const next = { ...previous }
      delete next[id]
      return next
    })
  }

  const addLine = () => setLines(previous => [...previous, blankLine()])

  const linkProduct = id => data => updateLine(id, {
    product_id:          data.id,
    product_name:        data.name,
    description:         data.name || "",
    ref_product_distrib: data.ref_product_brand || "",
    unit:                data.unit || "u",
    purchase_price:      data.purchase_price ?? 0,
    unit_price:          data.sale_price ?? 0,
    ...(data.cost_category ? { cost_category: data.cost_category } : {}),
  })

  const unlinkProduct = id => updateLine(id, { product_id: null, product_name: "" })

  // The line names the ouvrage rather than copying it, so the prices are its own.
  const linkOuvrage = id => blueprint => updateLine(id, {
    child_blueprint_id:   blueprint.id,
    child_blueprint_name: blueprint.name,
    child_line_count:     blueprint.line_count || 0,
    description:          blueprint.name || "",
    ref_product_distrib:  blueprint.ref_product_distrib || "",
    unit:                 blueprint.unit || "u",
    unit_price:           (blueprint.sale_cents ?? 0) / 100,
    purchase_price:       (blueprint.purchase_cents ?? 0) / 100,
    product_id:           null,
    product_name:         "",
    cost_category:        null,
  })

  const unlinkOuvrage = id => {
    updateLine(id, {
      child_blueprint_id: null, child_blueprint_name: "", child_line_count: 0,
      cost_category: "materials", unit_price: 0, purchase_price: 0,
    })
    setExpanded(previous => {
      const next = { ...previous }
      delete next[id]
      return next
    })
  }

  const setNature = (id, value) => {
    if (value === OUVRAGE) {
      updateLine(id, { cost_category: null, product_id: null, product_name: "" })
      return
    }
    const patch = {
      cost_category: value, child_blueprint_id: null, child_blueprint_name: "", child_line_count: 0,
      unit_price: 0, purchase_price: 0,
    }
    if (value === "labor") patch.unit = "h"
    updateLine(id, patch)
  }

  // Fetched on open: an entry can hold a whole chain, and most rows are never opened.
  const toggleExpanded = line => {
    if (expanded[line._clientId]) {
      setExpanded(previous => {
        const next = { ...previous }
        delete next[line._clientId]
        return next
      })
      return
    }
    if (!detailUrlFor || !line.child_blueprint_id) return
    setExpanded(previous => ({ ...previous, [line._clientId]: "loading" }))
    getRequest(detailUrlFor(line.child_blueprint_id))
      .then(data => setExpanded(previous => ({
        ...previous, [line._clientId]: linesOfDetail(data),
      })))
      .catch(() => {
        setExpanded(previous => {
          const next = { ...previous }
          delete next[line._clientId]
          return next
        })
        flashAlert(I18n.t("quotes.qline_service_blueprints.index.network_error"))
      })
  }

  const handleLineReorder = (fromIndex, toIndex) => {
    setLines(previous => {
      const visible    = previous.filter(line => !line._destroy)
      const tombstones = previous.filter(line => line._destroy)
      const reordered  = [...visible]
      const [moved]    = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, moved)
      return [...reordered, ...tombstones]
    })
  }

  const linesContainerRef = useRef(null)
  const sortableRef       = useRef(null)
  const reorderLatest     = useRef(handleLineReorder)
  reorderLatest.current   = handleLineReorder

  useEffect(() => {
    if (!isOpen || loading || !linesContainerRef.current) return
    sortableRef.current = new Sortable(linesContainerRef.current, {
      handle:     ".sortable-handle",
      draggable:  ".line-card",
      animation:  150,
      ghostClass: "opacity-40",
      onStart: event => { event.from._savedOrder = [...event.from.children] },
      onEnd: event => {
        const saved = event.from._savedOrder
        if (saved) {
          saved.forEach(child => event.from.appendChild(child))
          delete event.from._savedOrder
        }
        const from = event.oldDraggableIndex ?? event.oldIndex
        const to   = event.newDraggableIndex ?? event.newIndex
        if (from !== to) reorderLatest.current(from, to)
      },
    })
    return () => { sortableRef.current?.destroy(); sortableRef.current = null }
  }, [isOpen, loading])

  const handleSave = () => {
    if (saving) return
    setSaving(true)

    const body = {
      quotes_qline_service_blueprint: {
        name:                name,
        ref_product_distrib: ref,
        description:         description,
        unit:                unit,
        qline_breakdown_blueprints_attributes: lines.map((line, index) => ({
          id:                               line.id || undefined,
          cost_category:                    isOuvrage(line) ? null : line.cost_category,
          description:                      line.description,
          ref_product_distrib:              line.ref_product_distrib,
          unit:                             line.unit,
          quantity:                         line.quantity,
          unit_price:                       isOuvrage(line) ? 0 : line.unit_price,
          purchase_price:                   isOuvrage(line) ? 0 : line.purchase_price,
          product_id:                       line.product_id || null,
          child_qline_service_blueprint_id: line.child_blueprint_id || null,
          order:                            index,
          _destroy:                         line._destroy ? "1" : undefined,
        })),
      },
    }

    const request = mode === "edit"
      ? patchRequest({ url: updateUrlFor(blueprintId), body })
      : postRequest({ url: saveUrl, body })

    request
      .then(response => {
        if (response && response.errors) {
          const message = Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors)
          flashAlert(message)
          return
        }
        flashNotice(mode === "edit"
          ? I18n.t("quotes.qline_service_blueprints.form.saved")
          : I18n.t("quotes.qline_service_blueprints.form.created"))
        onSaved && onSaved()
        onClose()
      })
      .catch(() => flashAlert(I18n.t("quotes.qline_service_blueprints.index.network_error")))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("quotes.qline_service_blueprints.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {mode === "edit"
            ? I18n.t("quotes.qline_service_blueprints.form.save")
            : I18n.t("quotes.qline_service_blueprints.form.create_service")}
        </Button>
      </div>
    </>
  )

  const natureOptions = current => {
    const base    = (Array.isArray(enabledCategories) && enabledCategories.length > 0) ? enabledCategories : COST_CATEGORIES
    const visible = current && !base.includes(current) && COST_CATEGORIES.includes(current) ? [...base, current] : base
    return visible.map(category => (
      <option key={category} value={category}>{I18n.t(`quotes.new.cost_category.${category}`)}</option>
    ))
  }

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit"
        ? I18n.t("quotes.qline_service_blueprints.form.eyebrow_edit")
        : (mode === "duplicate"
            ? I18n.t("quotes.qline_service_blueprints.form.eyebrow_duplicate")
            : I18n.t("quotes.qline_service_blueprints.form.eyebrow_new"))}
      title   = {mode === "edit"
        ? (name || I18n.t("quotes.qline_service_blueprints.form.title_fallback"))
        : (mode === "duplicate"
            ? (name || I18n.t("quotes.qline_service_blueprints.form.title_duplicate"))
            : I18n.t("quotes.qline_service_blueprints.form.title_new"))}
      footer  = {footer}
    >
      {loading && (
        <div className="py-12 text-center text-sm text-slate-500">
          <FontAwesomeIcon icon="spinner" spin /> {I18n.t("quotes.qline_service_blueprints.form.loading")}
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{I18n.t("quotes.qline_service_blueprints.form.ref_label")}</label>
              <input
                value       = {ref}
                onChange    = {e => setRef(e.target.value.toUpperCase())}
                placeholder = {I18n.t("quotes.qline_service_blueprints.form.ref_placeholder")}
                className   = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm font-mono tabular-nums"
              />
            </div>
            <div className="col-span-7">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {I18n.t("quotes.qline_service_blueprints.form.name_label")} <span className="text-rose-500">*</span>
              </label>
              <input
                value       = {name}
                onChange    = {e => setName(e.target.value)}
                placeholder = {I18n.t("quotes.qline_service_blueprints.form.name_placeholder")}
                className   = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{I18n.t("quotes.qline_service_blueprints.form.unit_label")}</label>
              <input
                value       = {unit}
                onChange    = {e => setUnit(e.target.value)}
                placeholder = {I18n.t("quotes.qline_service_blueprints.form.unit_placeholder")}
                className   = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
              />
            </div>
            <div className="col-span-12">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {I18n.t("quotes.qline_service_blueprints.form.description_label")} <span className="text-slate-400 font-normal">{I18n.t("quotes.qline_service_blueprints.form.description_hint")}</span>
              </label>
              <textarea
                ref         = {descriptionRef}
                value       = {description}
                onChange    = {e => setDescription(e.target.value)}
                rows        = "2"
                placeholder = {I18n.t("quotes.qline_service_blueprints.form.description_placeholder")}
                className   = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm placeholder:text-slate-400"
                style       = {{ overflow: "hidden", resize: "none" }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{I18n.t("quotes.qline_service_blueprints.form.breakdown")}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {I18n.t("quotes.qline_service_blueprints.form.line_count", { count: visibleLines.length })}
                </p>
              </div>
              <Button variant="primary" size="sm" onClick={addLine}>
                <FontAwesomeIcon icon="plus" /> {I18n.t("quotes.qline_service_blueprints.form.add_line")}
              </Button>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-visible">
              <div
                className = "grid items-center gap-x-2 px-1 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200"
                style     = {{ gridTemplateColumns: LINES_GRID_COLUMNS }}
              >
                <div />
                <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_ref")}</div>
                <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_category")}</div>
                <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_unit")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_qty")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_purchase")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_sale")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_coef")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_margin")}</div>
                <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_total")}</div>
                <div />
              </div>

              <div ref={linesContainerRef}>
                {visibleLines.map(line => {
                  const ouvrage    = isOuvrage(line)
                  const locked     = ouvrage || !!line.product_id
                  const qty        = parseFloat(line.quantity)       || 0
                  const sale       = parseFloat(line.unit_price)     || 0
                  const purchase   = parseFloat(line.purchase_price) || 0
                  const lineTotal  = qty * sale
                  const lineCoef   = purchase > 0 ? sale / purchase : null
                  const lineMarPct = sale > 0 ? ((sale - purchase) / sale) * 100 : 0
                  const open       = expanded[line._clientId]

                  return (
                    <div
                      key       = {line._clientId}
                      className = {`line-card border-t border-slate-100 first:border-t-0 hover:bg-slate-50/40 ${ouvrage ? "bg-indigo-50/30" : ""}`}
                    >
                      <div className="flex items-center gap-2 px-1 pt-2">
                        <span className="w-7 shrink-0 flex items-center justify-center">
                          {ouvrage && (
                            <button
                              type      = "button"
                              onClick   = {() => toggleExpanded(line)}
                              title     = {I18n.t("quotes.qline_service_blueprints.form.component_chip")}
                              className = "text-indigo-400 hover:text-indigo-700"
                            >
                              <FontAwesomeIcon icon={open ? "chevron-down" : "chevron-right"} />
                            </button>
                          )}
                        </span>
                        <input
                          value       = {line.description}
                          onChange    = {e => updateLine(line._clientId, { description: e.target.value })}
                          placeholder = {I18n.t("quotes.qline_service_blueprints.form.line_label_placeholder")}
                          className   = "flex-1 h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm font-medium text-slate-900 placeholder:font-normal placeholder:text-slate-400"
                        />
                        <div className="w-64 shrink-0">
                          {ouvrage ? (
                            <div className="flex items-center gap-1.5 h-9 px-2 rounded border border-indigo-200 bg-indigo-50 text-xs text-indigo-800">
                              <FontAwesomeIcon icon="sitemap" className="text-indigo-500" />
                              <span className="flex-1 truncate" title={line.child_blueprint_name}>
                                {line.child_blueprint_name}
                              </span>
                              <span className="shrink-0 text-indigo-400">
                                {I18n.t("quotes.qline_service_blueprints.form.line_count", { count: line.child_line_count })}
                              </span>
                              <button
                                type      = "button"
                                onClick   = {() => unlinkOuvrage(line._clientId)}
                                title     = {I18n.t("quotes.qline_service_blueprints.form.component_unlink")}
                                className = "text-indigo-600 hover:text-indigo-900"
                              >
                                <FontAwesomeIcon icon="xmark" />
                              </button>
                            </div>
                          ) : line.cost_category === null ? (
                            <ComponentPicker
                              size        = "sm"
                              excludeId   = {blueprintId}
                              placeholder = {I18n.t("quotes.qline_service_blueprints.form.component_search_placeholder")}
                              onSelect    = {linkOuvrage(line._clientId)}
                            />
                          ) : line.product_id ? (
                            <div className="flex items-center gap-1.5 h-9 px-2 rounded border border-emerald-200 bg-emerald-50 text-xs text-emerald-800">
                              <FontAwesomeIcon icon="box" className="text-emerald-500" />
                              <span className="flex-1 truncate" title={`${line.product_name} — ${I18n.t("quotes.qline_service_blueprints.form.product_linked")}`}>
                                {line.product_name}
                              </span>
                              <button
                                type      = "button"
                                onClick   = {() => unlinkProduct(line._clientId)}
                                title     = {I18n.t("quotes.qline_service_blueprints.form.product_unlink")}
                                className = "text-emerald-600 hover:text-emerald-900"
                              >
                                <FontAwesomeIcon icon="xmark" />
                              </button>
                            </div>
                          ) : (
                            <ProductPicker
                              placeholder = {I18n.t("quotes.qline_service_blueprints.form.product_search_placeholder")}
                              onSelect    = {linkProduct(line._clientId)}
                            />
                          )}
                        </div>
                      </div>

                      <div
                        className = "grid items-center gap-x-2 px-1 pb-2 pt-1"
                        style     = {{ gridTemplateColumns: LINES_GRID_COLUMNS }}
                      >
                        <span
                          className = "sortable-handle flex items-center justify-center h-9 w-full cursor-grab text-slate-300 hover:text-slate-500"
                          title     = {I18n.t("quotes.qline_service_blueprints.form.drag_handle")}
                        >
                          <FontAwesomeIcon icon="grip-vertical" />
                        </span>

                        <input
                          value       = {line.ref_product_distrib}
                          maxLength   = {30}
                          onChange    = {e => updateLine(line._clientId, { ref_product_distrib: e.target.value })}
                          placeholder = {I18n.t("quotes.qline_service_blueprints.form.line_ref_placeholder")}
                          className   = "w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm font-mono"
                        />

                        <select
                          value     = {ouvrage || line.cost_category === null ? OUVRAGE : line.cost_category}
                          onChange  = {e => setNature(line._clientId, e.target.value)}
                          className = "w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm bg-white"
                        >
                          {natureOptions(line.cost_category)}
                          <option value={OUVRAGE}>{I18n.t("quotes.qline_service_blueprints.form.component_chip")}</option>
                        </select>

                        <input
                          value     = {line.unit}
                          onChange  = {e => updateLine(line._clientId, { unit: e.target.value })}
                          disabled  = {ouvrage || line.cost_category === "labor"}
                          className = {`w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm ${ouvrage || line.cost_category === "labor" ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                        />

                        <input
                          type      = "number"
                          step      = "0.01"
                          value     = {line.quantity}
                          onChange  = {e => updateLine(line._clientId, { quantity: e.target.value })}
                          className = "w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm text-right tabular-nums"
                        />

                        <input
                          type      = "number"
                          step      = "0.01"
                          value     = {line.purchase_price}
                          disabled  = {locked}
                          title     = {ouvrage ? I18n.t("quotes.qline_service_blueprints.form.component_chip") : (line.product_id ? I18n.t("quotes.qline_service_blueprints.form.product_linked") : undefined)}
                          onChange  = {e => updateLine(line._clientId, { purchase_price: e.target.value })}
                          className = {`w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm text-right tabular-nums ${locked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                        />

                        <input
                          type      = "number"
                          step      = "0.01"
                          value     = {line.unit_price}
                          disabled  = {locked}
                          title     = {ouvrage ? I18n.t("quotes.qline_service_blueprints.form.component_chip") : (line.product_id ? I18n.t("quotes.qline_service_blueprints.form.product_linked") : undefined)}
                          onChange  = {e => updateLine(line._clientId, { unit_price: e.target.value })}
                          className = {`w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white text-sm text-right tabular-nums ${locked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                        />

                        <div className="px-2 text-right tabular-nums text-sm text-slate-700">{coefFmt(lineCoef)}</div>
                        <div className="text-right"><MarginBadge pct={lineMarPct} /></div>
                        <div className="px-2 text-right tabular-nums text-sm font-semibold text-slate-900">{money(lineTotal)}</div>

                        <IconButton tone="danger" size="md" title={I18n.t("quotes.qline_service_blueprints.form.remove_line")} onClick={() => removeLine(line._clientId)}>
                          <FontAwesomeIcon icon="xmark" />
                        </IconButton>
                      </div>

                      {(open === "loading" || (Array.isArray(open) && open.length === 0)) && (
                        <div className="pl-10 pr-3 pb-3 text-xs text-slate-500">
                          {open === "loading" ? (
                            <span><FontAwesomeIcon icon="spinner" spin /> {I18n.t("quotes.qline_service_blueprints.form.loading")}</span>
                          ) : (
                            <span>{I18n.t("quotes.qline_service_blueprints.form.empty_lines")}</span>
                          )}
                        </div>
                      )}

                      {Array.isArray(open) && open.length > 0 && (
                        <div className="px-1 pb-2">
                          <div className="rounded-md bg-slate-50 py-1">
                            <BlueprintTree
                              nodes         = {open}
                              factor        = {qty}
                              columns       = {LINES_GRID_COLUMNS}
                              span          = {TREE_SPAN}
                              indent        = {36}
                              renderFigures = {treeFigures}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                {visibleLines.length === 0 && (
                  <div className="py-10 text-center text-sm text-slate-500">
                    {I18n.t("quotes.qline_service_blueprints.form.empty_lines")}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200">
                <button
                  type      = "button"
                  onClick   = {addLine}
                  className = "w-full py-2.5 text-sm text-slate-500 hover:text-brand-600 hover:bg-brand-50 flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon="plus" className="text-xs" /> {I18n.t("quotes.qline_service_blueprints.form.add_another_line")}
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <div className="w-[420px] bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{I18n.t("quotes.qline_service_blueprints.form.totals_heading")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <span className="text-slate-600">{I18n.t("quotes.qline_service_blueprints.form.sale_excl_tax")}</span>
                  <span className="tabular-nums font-semibold text-slate-900 text-right">{money(rollup.total)}</span>

                  <span className="text-slate-600">{I18n.t("quotes.qline_service_blueprints.form.purchase_cost")}</span>
                  <span className="tabular-nums text-slate-700 text-right">{money(rollup.cost)}</span>

                  <span className="text-slate-600">{I18n.t("quotes.qline_service_blueprints.form.margin")}</span>
                  <span className="tabular-nums text-slate-900 font-medium text-right">{money(rollup.margin)}</span>
                </div>
                <div className="h-px bg-slate-200 my-3" />
                <div className="grid grid-cols-2 gap-x-6 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">{I18n.t("quotes.qline_service_blueprints.form.global_coef")}</div>
                    <div className="text-lg font-bold tabular-nums text-slate-900">{coefFmt(rollup.coef)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">{I18n.t("quotes.qline_service_blueprints.form.global_margin")}</div>
                    <div className="text-lg font-bold tabular-nums text-slate-900">{rollup.marginPct.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default BlueprintFormModal
